import cn from 'classnames';

import React, { useCallback, useEffect } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import useScrollOffset from 'hooks/useScrollOffset';
import mergeRefs from 'utils/mergeRefs';
import styles from './FadingContainer.module.scss';

export interface FadingContainerProps {
  axis: 'x' | 'y';
  backgroundColor: string;
  children: React.ReactNode;
  className?: string;
  threshold?: number;
}

const FadingContainer: React.FC<FadingContainerProps> = ({
  axis,
  backgroundColor,
  children,
  className,
  threshold = 0.25,
}) => {
  const {
    calculateScrollPercentage,
    percentage,
    ref: scrollRef,
  } = useScrollOffset<HTMLElement>();
  const { ref: containerRef, width, height } = useResizeDetector();

  const onlyChild = React.Children.only(children);

  useEffect(() => {
    calculateScrollPercentage();
  }, [width, height, calculateScrollPercentage]);

  const checkRef = useCallback((el: HTMLElement | null) => {
    if (el && !(el instanceof HTMLElement)) {
      throw new Error(
        'FadingContainer expects its only child to have an HTMLElement ref',
      );
    }
  }, []);

  if (!React.isValidElement(onlyChild)) {
    throw new Error(
      'FadingContainer expects a single ReactElement as children',
    );
  }

  return (
    <div
      className={cn(
        styles.root,
        styles[`root__${axis}`],
        {
          [styles.root__start]: (percentage[axis] ?? -Infinity) >= threshold,
          [styles.root__end]: (percentage[axis] ?? Infinity) <= 1 - threshold,
        },
        className,
      )}
      ref={containerRef}
      style={{ ['--background-color' as string]: backgroundColor }}
    >
      {React.cloneElement(onlyChild as React.ReactElement, {
        ref: mergeRefs([scrollRef, checkRef]),
      })}
    </div>
  );
};

export default FadingContainer;
